import React from 'react';
import { X, Download, Pencil, ArrowRight, Braces, Box, GitBranch, Printer } from 'lucide-react';
import { ProblemPdfData, ProblemPdfTheme } from '../../utils/lldPdfGenerator';

interface PracticeSummaryModalProps { data: ProblemPdfData; onClose: () => void; onDownload: (theme: ProblemPdfTheme) => void; }

const accessSymbol = (mod: string): string => mod === 'public' ? '+' : mod === 'private' ? '-' : mod === 'protected' ? '#' : mod;

const Section: React.FC<{ n: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ n, title, subtitle, children }) => (
 <section className="lld-summary-section">
  <header className="lld-summary-section__header">
   <span>{String(n).padStart(2, '0')}</span>
   <div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
  </header>
  <div className="lld-summary-section__body">{children}</div>
 </section>
);

const Empty: React.FC<{ text: string }> = ({ text }) => <div className="lld-summary-empty">{text}</div>;

const PracticeSummaryModal: React.FC<PracticeSummaryModalProps> = ({ data, onClose, onDownload }) => {
 const entityName = (id: string) => data.entities.find((entity) => entity.id === id)?.name || 'Unknown';
 const nonEmptyFiles = data.codeFiles.filter((file) => (file.content || '').trim().length > 0);
 const requirementGroups = [
  { key: 'functional', label: 'Functional' },
  { key: 'non-functional', label: 'Non-functional' },
  { key: 'assumption', label: 'Assumptions' },
 ] as const;

 return (
  <div className="lld-summary-overlay" onClick={onClose}>
   <div className="lld-summary-modal" onClick={(event) => event.stopPropagation()}>
    <header className="lld-summary-toolbar">
     <div><span>Practice summary</span><h2>Review your design before exporting</h2></div>
     <button onClick={onClose} title="Close preview" aria-label="Close preview"><X /></button>
    </header>

    <div className="lld-summary-document">
     <div className="lld-summary-cover">
      <div className="lld-summary-cover__copy">
       <span>Low-level design · {data.mode} mode</span>
       <h1>{data.title}</h1>
       <p>{data.description || 'No problem statement was provided.'}</p>
       <div className="lld-summary-tags">
        {data.difficulty && <span>{data.difficulty}</span>}
        {data.category && <span>{data.category}</span>}
        <span>{data.activeLanguage}</span>
       </div>
      </div>
      <div className="lld-summary-metrics" aria-label="Summary counts">
       <div><strong>{data.requirements.length}</strong><span>requirements</span></div>
       <div><strong>{data.entities.length}</strong><span>entities</span></div>
       <div><strong>{data.relationships.length}</strong><span>relationships</span></div>
       <div><strong>{nonEmptyFiles.length}</strong><span>code files</span></div>
      </div>
     </div>

     <Section n={1} title="Problem brief" subtitle="The scope being designed">
      <p className="lld-summary-prose">{data.description || 'No problem statement was provided.'}</p>
      {data.clarifications.length > 0 && <div className="lld-summary-clarifications">{data.clarifications.map((item, index) => <div key={index}><strong>{item.question}</strong><p>{item.answer}</p></div>)}</div>}
     </Section>

     <Section n={2} title="Requirements" subtitle="What the solution must accomplish and protect">
      {data.requirements.length === 0 ? <Empty text="No requirements captured." /> : (
       <div className="lld-summary-requirements">
        {requirementGroups.map((group) => {
         const items = data.requirements.filter((requirement) => requirement.category === group.key);
         if (!items.length) return null;
         return <div key={group.key}><header><span>{group.label}</span><strong>{items.length}</strong></header><ul>{items.map((item, index) => <li key={index}>{item.text}</li>)}</ul></div>;
        })}
       </div>
      )}
     </Section>

     <Section n={3} title="Domain model" subtitle="Entities and the responsibilities they own">
      {data.entities.length === 0 ? <Empty text="No entities identified." /> : (
       <div className="lld-summary-entities">
        {data.entities.map((entity) => {
         const responsibility = data.responsibilities[entity.id] || { attributes: [], methods: [] };
         return <article key={entity.id}>
          <header><span><Box /></span><div><h4>{entity.name}</h4>{entity.description && <p>{entity.description}</p>}</div></header>
          <div className="lld-summary-members">
           <div><strong>Fields</strong>{responsibility.attributes.length ? responsibility.attributes.map((attribute, index) => <code key={index}>{accessSymbol(attribute.accessModifier)} {attribute.name}: <i>{attribute.type}</i></code>) : <small>None defined</small>}</div>
           <div><strong>Methods</strong>{responsibility.methods.length ? responsibility.methods.map((method, index) => <code key={index}>{accessSymbol(method.accessModifier)} {method.name}({method.parameters.map((parameter) => `${parameter.name}: ${parameter.type}`).join(', ')}): <i>{method.returnType}</i></code>) : <small>None defined</small>}</div>
          </div>
         </article>;
        })}
       </div>
      )}
     </Section>

     <Section n={4} title="Relationships" subtitle="How the domain objects collaborate">
      {data.relationships.length === 0 ? <Empty text="No relationships defined." /> : (
       <div className="lld-summary-relationships">
        {data.relationships.map((relationship, index) => <div key={index}>
         <GitBranch /><strong>{entityName(relationship.sourceEntityId)}</strong>
         <span>{relationship.type}<ArrowRight /></span>
         <strong>{entityName(relationship.targetEntityId)}</strong>
         <em>{relationship.cardinality.replace('-to-', ' → ')}</em>
        </div>)}
       </div>
      )}
     </Section>

     <Section n={5} title="UML class view" subtitle="The final structural model">
      {data.uml.elements.length === 0 ? <Empty text="No UML elements created." /> : (
       <div className="lld-summary-uml">
        {data.uml.elements.map((element, index) => <article key={index}>
         <header><small>{element.type.replace('-', ' ')}</small><h4>{element.name}</h4></header>
         <div>{element.fields.length ? element.fields.map((field, fieldIndex) => <code key={fieldIndex}>{field.access} {field.name}: <i>{field.type}</i></code>) : <small>No fields</small>}</div>
         <div>{element.methods.length ? element.methods.map((method, methodIndex) => <code key={methodIndex}>{method.access} {method.signature}</code>) : <small>No methods</small>}</div>
        </article>)}
       </div>
      )}
     </Section>

     <Section n={6} title="Implementation" subtitle={`Language: ${data.activeLanguage} · ${nonEmptyFiles.length} file${nonEmptyFiles.length === 1 ? '' : 's'}`}>
      {nonEmptyFiles.length === 0 ? <Empty text="No implementation written." /> : (
       <div className="lld-summary-code-files">
        {nonEmptyFiles.map((file, index) => <details key={file.path} open={index === 0}>
         <summary><span><Braces />{file.path}</span><small>{file.content.split('\n').length} lines</small></summary>
         <pre><code>{file.content}</code></pre>
        </details>)}
       </div>
      )}
     </Section>

    </div>

    <footer className="lld-summary-footer">
     <span>The PDF uses this same structure and includes every code file.</span>
     <button onClick={onClose}><Pencil />Back to edit</button>
     <button onClick={() => onDownload('branded')}><Download />Branded dark</button>
     <button onClick={() => onDownload('interview')}><Printer />Interview white</button>
    </footer>
   </div>
  </div>
 );
};

export default PracticeSummaryModal;
